import { RoutePermissionsMap } from '@lp/core/routing/guards/types';
import {
  AdminPermissions,
  ArchiveCasesPermissions,
  BulkAssignmentPermissions,
  CaseBoardPermissions,
  CaseDetailsPermissions,
  ChatViewPermissions,
  MultiCaseSuggestPermissions,
  ReleaseNotesPermissions,
  RouteBuilderPermissions,
  SchedulerPermissions,
} from '@/permissions';

type PermissionType =
  | AdminPermissions
  | CaseBoardPermissions
  | ChatViewPermissions
  | ArchiveCasesPermissions
  | BulkAssignmentPermissions
  | CaseDetailsPermissions
  | RouteBuilderPermissions
  | MultiCaseSuggestPermissions
  | ReleaseNotesPermissions
  | SchedulerPermissions;

const getNoAccessConfig = (permissions: PermissionType[]) => ({
  permissions,
  redirectTo: '/no-access',
});

const providersConfigurationViewConfig = getNoAccessConfig([AdminPermissions.ProvidersConfigurationView]);
const providersConfigurationCreateConfig = getNoAccessConfig([AdminPermissions.ProvidersConfigurationCreate]);
const tmdxUsersConfig = getNoAccessConfig([AdminPermissions.TmdxUsersView]);
const hospitalViewConfig = getNoAccessConfig([AdminPermissions.HospitalView]);
const hospitalCreateConfig = getNoAccessConfig([AdminPermissions.HospitalCreate]);
const opoViewConfig = getNoAccessConfig([AdminPermissions.OpoView]);
const opoCreateConfig = getNoAccessConfig([AdminPermissions.OpoCreate]);
const txViewConfig = getNoAccessConfig([AdminPermissions.TxCenterView]);
const txCreateConfig = getNoAccessConfig([AdminPermissions.TxCenterCreate]);

export const routePermissionConfig: RoutePermissionsMap = {
  // Home
  '/': { permissions: [CaseBoardPermissions.CaseBoardRead], redirectTo: '/profile' },

  // Case Details
  '/case/:caseId': getNoAccessConfig([CaseDetailsPermissions.CaseRead]),
  '/case/:caseId/planner': getNoAccessConfig([RouteBuilderPermissions.ViewBuilder]),
  '/case/:caseId/planner/change-log': getNoAccessConfig([RouteBuilderPermissions.ViewBuilder]),
  '/case/:caseId/planner/device-tracking-log': getNoAccessConfig([RouteBuilderPermissions.DeviceTrackingLogView]),
  '/case/:caseId/*': getNoAccessConfig([CaseDetailsPermissions.CaseRead]),

  // Chat
  '/chats': getNoAccessConfig([ChatViewPermissions.ViewChat]),

  // Archive
  '/archive': getNoAccessConfig([ArchiveCasesPermissions.ViewArchive]),

  // Bulk Assignment
  '/bulk-assignment': getNoAccessConfig([BulkAssignmentPermissions.ViewBulkAssignment]),

  // Multi Case Suggest
  '/multi-case-suggest': getNoAccessConfig([MultiCaseSuggestPermissions.ReadMCS]),
  '/multi-case-suggest/:multiSuggestId': getNoAccessConfig([MultiCaseSuggestPermissions.ReadMCS]),

  // Scheduler
  '/schedule/cs': getNoAccessConfig([SchedulerPermissions.CSSchedulesRead, SchedulerPermissions.CSSchedulesWrite]),
  '/schedule/cs/settings': getNoAccessConfig([SchedulerPermissions.CSSchedulesSettingsRead]),
  '/schedule/s-li': getNoAccessConfig([SchedulerPermissions.SSchedulesRead, SchedulerPermissions.SSchedulesWrite]),
  '/schedule/s-li/settings': getNoAccessConfig([SchedulerPermissions.SSchedulesSettingsWrite]),
  '/schedule/s-htlu': getNoAccessConfig([SchedulerPermissions.SSchedulesRead, SchedulerPermissions.SSchedulesWrite]),
  '/schedule/s-htlu/settings': getNoAccessConfig([SchedulerPermissions.SSchedulesSettingsWrite]),
  '/schedule/cc': getNoAccessConfig([
    SchedulerPermissions.CCSchedulesRead,
    SchedulerPermissions.CCSchedulesWrite,
    SchedulerPermissions.CCMyScheduleRead,
    SchedulerPermissions.CCMyScheduleWrite,
  ]),
  '/schedule/cc/settings': getNoAccessConfig([SchedulerPermissions.CCSchedulesSettingsRead]),
  '/schedule/oms': getNoAccessConfig([SchedulerPermissions.OMSSchedulesRead, SchedulerPermissions.OMSSchedulesWrite]),
  '/schedule/oms/settings': getNoAccessConfig([SchedulerPermissions.OMSSchedulesSettingsRead]),

  // Admin > Employees
  '/admin/users/employees': tmdxUsersConfig,
  '/admin/users/employees/:employeeId': tmdxUsersConfig,

  // Admin > Contractors
  '/admin/users/contractors': tmdxUsersConfig,
  '/admin/users/contractors/:employeeId': tmdxUsersConfig,

  // Admin > DH Locations
  '/admin/hospitals/locations': hospitalViewConfig,
  '/admin/hospitals/locations/create': hospitalCreateConfig,
  '/admin/hospitals/locations/:locationId': hospitalViewConfig,

  // Admin > DH Contacts
  '/admin/hospitals/contacts': hospitalViewConfig,
  '/admin/hospitals/contacts/create': hospitalCreateConfig,
  '/admin/hospitals/contacts/:contactId': hospitalViewConfig,

  // Admin > OPO Locations
  '/admin/opos/locations': opoViewConfig,
  '/admin/opos/locations/create': opoCreateConfig,
  '/admin/opos/locations/:locationId': opoViewConfig,

  // Admin > OPO Contacts
  '/admin/opos/contacts': opoViewConfig,
  '/admin/opos/contacts/create': opoCreateConfig,
  '/admin/opos/contacts/:contactId': opoViewConfig,

  // Admin > Hubs
  '/admin/hubs/locations': getNoAccessConfig([AdminPermissions.NopHubView]),
  '/admin/hubs/locations/create': getNoAccessConfig([AdminPermissions.NopHubCreate]),
  '/admin/hubs/locations/:locationId': getNoAccessConfig([AdminPermissions.NopHubView]),

  // Admin > TX Centers
  '/admin/customers/tx_centers': txViewConfig,
  '/admin/customers/tx_centers/create': txCreateConfig,
  '/admin/customers/tx_centers/:txCenterId': txViewConfig,

  // Admin > TX Users
  '/admin/customers/tx_users': txViewConfig,
  '/admin/customers/tx_users/create': txCreateConfig,
  '/admin/customers/tx_users/:tx_usersId': txViewConfig,

  // Admin > OCS Consoles
  '/admin/devices/ocs_consoles': getNoAccessConfig([AdminPermissions.OcsConsolesView]),
  '/admin/devices/ocs_consoles/create': getNoAccessConfig([AdminPermissions.OcsConsolesCreate]),
  '/admin/devices/ocs_consoles/:consoleId': getNoAccessConfig([AdminPermissions.OcsConsolesView]),
  '/admin/devices/ocs_perfusion_sets': getNoAccessConfig([AdminPermissions.OcsPerfusionsSetsView]),

  // Admin > Logistics Providers
  '/admin/logistics_providers/supplemental_lift': providersConfigurationViewConfig,
  '/admin/logistics_providers/supplemental_lift/create': providersConfigurationCreateConfig,
  '/admin/logistics_providers/supplemental_lift/:supplementalLiftProviderId': providersConfigurationViewConfig,
  '/admin/logistics_providers/tx_center': providersConfigurationViewConfig,
  '/admin/logistics_providers/tx_center/create': providersConfigurationCreateConfig,
  '/admin/logistics_providers/tx_center/:providerId': providersConfigurationViewConfig,

  // Admin > Configurations
  '/admin/configuration/banned_tail_numbers': providersConfigurationViewConfig,
  '/admin/configuration/banned_tail_numbers/create': providersConfigurationCreateConfig,
  '/admin/configuration/banned_tail_numbers/:bannedTailNumberId': providersConfigurationViewConfig,
  '/admin/configuration/banned_drivers': providersConfigurationViewConfig,
  '/admin/configuration/banned_drivers/create': providersConfigurationCreateConfig,
  '/admin/configuration/banned_drivers/:bannedDriverId': providersConfigurationViewConfig,

  // Admin > Regions
  '/admin/regions': getNoAccessConfig([AdminPermissions.RegionsRead]),
  '/admin/regions/:regionId': getNoAccessConfig([AdminPermissions.RegionsRead]),

  // Admin > Release Notes
  '/admin/release-notes': getNoAccessConfig([ReleaseNotesPermissions.Read]),
};
